// Kho báo cáo công việc hằng ngày + cấu hình khung giờ nhắc: localStorage (bản mẫu; bản thật gửi / đồng bộ với portal).
// Tách khỏi kho Công việc để chip nhắc ở khung chính không phải nạp cả dữ liệu mẫu công việc.
import { useSyncExternalStore } from 'react';
import { DEFAULT_REPORT_CONFIG, configError, type ReportConfig } from './reportRules';

const KEY = 'swchat.work.report.v1';
const WORK_KEY = 'swchat.work.v1';

export interface ReportItem {
  id: string;
  /** Đầu việc (từ KPI) hoặc việc phát sinh. */
  name: string;
  group: string;
  /** Việc phát sinh do người dùng thêm. */
  adhoc: boolean;
  /** Tiến độ hôm nay, 0–100. */
  progress: number;
}

export interface DailyReport {
  /** Ngày báo cáo YYYY-MM-DD (giờ máy). */
  date: string;
  personId: string;
  items: ReportItem[];
  difficulties: string;
  proposals: string;
  submittedAt: string;
}

interface ReportState {
  config: ReportConfig;
  reports: DailyReport[];
}

const pad = (n: number) => String(n).padStart(2, '0');
export const dateKey = (d: Date = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function load(): ReportState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw) as Partial<ReportState>;
      const config = s.config && configError(s.config) === undefined ? s.config : DEFAULT_REPORT_CONFIG;
      return { config, reports: Array.isArray(s.reports) ? s.reports : [] };
    }
  } catch {
    // localStorage bị chặn hoặc hỏng → dùng mặc định.
  }
  return { config: DEFAULT_REPORT_CONFIG, reports: [] };
}

let state: ReportState = load();
const listeners = new Set<() => void>();

function commit(next: ReportState) {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Không lưu được — vẫn chạy trong bộ nhớ.
  }
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));
export const getReports = () => state;
export const useReports = () => useSyncExternalStore(subscribe, getReports, getReports);

/** Tài khoản đang xem thử của màn Công việc (đọc nhẹ từ kho Công việc; mặc định như kho đó). */
export function currentPersonId(): string {
  try {
    const raw = localStorage.getItem(WORK_KEY);
    if (raw) {
      const id = (JSON.parse(raw) as { meId?: unknown }).meId;
      if (typeof id === 'string') return id;
    }
  } catch {
    // dùng mặc định
  }
  return 'binh';
}

export const reportOf = (s: ReportState, personId: string, date: string = dateKey()) => s.reports.find((r) => r.personId === personId && r.date === date);

let counter = 0;
export const newItemId = () => `r${Date.now().toString(36)}${(counter++).toString(36)}`;

export const reports = {
  /** Gửi (hoặc cập nhật) báo cáo của một người cho một ngày. */
  submit(r: Omit<DailyReport, 'submittedAt'>) {
    const rest = state.reports.filter((x) => !(x.personId === r.personId && x.date === r.date));
    commit({ ...state, reports: [{ ...r, submittedAt: new Date().toISOString() }, ...rest].slice(0, 120) });
  },
  /** Admin đổi khung giờ nhắc; trả lỗi nếu không hợp lệ. */
  setConfig(config: ReportConfig): string | undefined {
    const err = configError(config);
    if (err) return err;
    commit({ ...state, config });
    return undefined;
  },
  reset() {
    commit({ config: DEFAULT_REPORT_CONFIG, reports: [] });
  },
};
